import { CommonModule, NgIf } from '@angular/common';
import { Component } from '@angular/core';
import { PageTitleService } from '../../../../Utils/_services/page-title/page-title.service';
import { IBug, IBugReportingEntityDto } from './bug-report.model';
import { FormsModule, NgModel } from '@angular/forms';
import { BugReportingService } from '../../../../Utils/_services/bug-report/bug-reporting.service';
import { ToastrService, ToastrModule } from 'ngx-toastr';
import { ICustomError } from '../../../../Utils/_models/network-response';
import { AdminNavDetailsComponent } from "../../layout/admin-nav-details/admin-nav-details.component";


@Component({
  selector: 'app-bug-reporting',
  standalone: true,
  imports: [CommonModule, FormsModule, ToastrModule, AdminNavDetailsComponent, NgIf],
  templateUrl: './bug-reporting.component.html',
  styleUrl: './bug-reporting.component.css'
})
export class BugReportingComponent {

  // properties
  viewMode: 'card' | 'table' = 'table';
  showModal = false;
  selectedBug: IBugReportingEntityDto | null = null;
  bugs: IBugReportingEntityDto[] = [];
  pageNumber = 1;
  pageSize = 5;
  totalItems = 0;
  totalPages = 0;
  isLoading = false;
  isSubmitting = false;

  constructor(
    private pageTitle: PageTitleService,
    private bugService: BugReportingService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.pageTitle.setTitle('Bug Reporting');
    this.loadBugs();
  }

  loadBugs() {
    this.isLoading = true;
    this.bugService.getAllBugs(this.pageNumber, this.pageSize).subscribe({
      next: (result) => {
        this.bugs = result.data.items;
        this.totalItems = result.data.totalCount;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        this.isLoading = false;
      },
      error: (err: ICustomError) => {
        this.isLoading = false;
        console.error('Failed to load bugs:', err.error.msg);
        this.toastr.error('Error loading bugs. Please try again.', 'Error');
      }
    });
  }

  updateBug() {
    if (this.selectedBug && this.selectedBug.id) {
      this.isSubmitting = true;
      this.bugService.update(this.selectedBug.id, {
        status: this.selectedBug.status
      }).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.toastr.info('Bug updated successfully!', 'Success');
          this.clearCached();
          this.closeModal();
        },
        error: (err) => {
          this.isSubmitting = false;
          console.error('Failed to update bug:', err);
          this.toastr.error(err.error?.message || 'Failed to update bug. Please try again.', 'Error');
        }
      });
    }
  }

  clearCached(): void {
    this.bugService.clearCached().subscribe({
      next: (response) => {
        this.loadBugs();
      },
      error: (err: ICustomError) => {
        console.log("Failed to clear cached");
        this.toastr.error("Error clearing cached, Please try again");
      }
    })
  }

  toggleView() {
    this.viewMode = this.viewMode === 'card' ? 'table' : 'card';
  }

  viewBug(bug: IBugReportingEntityDto) {
    this.selectedBug = { ...bug, id: String(bug.id), status: this.getStatus(bug.status) };
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.selectedBug = null;
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.pageNumber = page;
      this.loadBugs();
    }
  }

  getVisiblePages(): number[] {
    const visiblePages: number[] = [];
    const maxVisiblePages = 5;

    if (this.totalPages <= maxVisiblePages) {
      // Show all pages if total pages is small (excluding first and last as they're handled separately)
      for (let i = 2; i < this.totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      // Show pages around current page
      let startPage = Math.max(2, this.pageNumber - 1);
      let endPage = Math.min(this.totalPages - 1, this.pageNumber + 1);

      // Ensure we don't include first and last page (they're handled separately)
      for (let i = startPage; i <= endPage; i++) {
        if (i > 1 && i < this.totalPages) {
          visiblePages.push(i);
        }
      }
    }

    return visiblePages;
  }

  getStatusClass(status: string): string {
    status = status.toLowerCase();
    switch (status) {
      case 'open': return 'status-open';
      case 'in progress': return 'status-progress';
      case 'pending': return 'status-pending';
      case 'closed': return 'status-closed';
      default: return '';
    }
  }

  getPriorityClass(priority: string): string {
    priority = priority.toLowerCase();
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return '';
    }
  }


  getStatus(status?: string): string {
    status = status?.trim().toLowerCase();
    switch (status) {
      case 'closed':
      case 'completed':
      case 'resolved':
        return 'closed';
      case 'progress':
      case 'in-progress':
      case 'working':
        return 'in-progress';
      case 'open':
      case 'new':
      case 'pending':
        return 'open';
      default: return 'n/a';
    }
  }
}
